import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Users, Loader2, RefreshCw, Play, CheckCircle2, AlertTriangle } from "lucide-react";

// Super-admin panel: sync every eligible member into their Resend segment (Ambassadors, Founding
// Members, Dealers, Buyer Pass, Paid Members, Free / No Plan). New signups + plan/status changes
// sync automatically; this is the one-time backfill for accounts that predate the sync. Writes only
// run in PRODUCTION (the Resend account is shared, so preview/QA members are never pushed live).
export function ResendSyncPanel() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const pollRef = useRef(null);

  const loadStatus = useCallback(() => {
    api.get("/admin/resend-sync").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadStatus]);

  const runPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post("/admin/resend-sync/preview");
      setPreview(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(""); }
  };

  const runBackfill = async () => {
    setBusy("backfill");
    try {
      await api.post("/admin/resend-sync/backfill");
      toast.success("Backfill started — syncing all eligible members to Resend.");
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get("/admin/resend-sync");
          setStatus(data);
          if (data?.last_run?.status && data.last_run.status !== "running") {
            clearInterval(pollRef.current); pollRef.current = null;
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(""); }
  };

  const run = status?.last_run || {};
  const running = run.status === "running";
  const segments = status?.segments || [];

  return (
    <div className="border border-sniper bg-surface p-5" data-testid="resend-sync-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-olive-bright" strokeWidth={1.7} />
          <div>
            <h3 className="font-heading font-bold uppercase tracking-tight text-lg">Resend Audience Sync</h3>
            <p className="text-xs font-body text-secondary-foreground mt-0.5">
              Mirror members into Resend segments by member type. Signups + plan/status changes sync automatically.
            </p>
          </div>
        </div>
        <button onClick={loadStatus} data-testid="resend-sync-refresh" className="text-muted-foreground hover:text-foreground transition-colors" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-wide" data-testid="resend-sync-status">
        <span className={`px-2 py-0.5 border ${status?.enabled ? "border-olive/60 text-olive-bright" : "border-blaze/60 text-blaze"}`}>
          {status?.enabled ? "Sync enabled" : "Sync disabled"}
        </span>
        <span className={`px-2 py-0.5 border ${status?.writes_allowed ? "border-olive/60 text-olive-bright" : "border-amber-400/60 text-amber-400"}`}>
          {status?.writes_allowed ? "Production · writes live" : "Preview · writes off (prod only)"}
        </span>
      </div>

      {/* segments */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {segments.map((s) => (
          <div key={s.key} data-testid={`resend-sync-segment-${s.key}`} className="border border-sniper bg-input px-3 py-2">
            <p className="font-heading uppercase text-[12px] tracking-tight text-foreground">{s.name}</p>
            <p className="font-mono text-[10px] text-muted-foreground truncate">{s.id ? "✓ live" : "not created"}{preview ? ` · ${preview.by_segment?.[s.key] ?? 0}` : ""}</p>
          </div>
        ))}
      </div>

      {preview && (
        <p className="mt-3 text-xs font-mono text-secondary-foreground" data-testid="resend-sync-preview-result">
          Scanned {preview.scanned} · <span className="text-olive-bright">{preview.eligible} eligible</span> · {preview.excluded} excluded (test/demo/internal/admin/disabled)
        </p>
      )}

      {/* run state */}
      {run.status && (
        <div className="mt-3 border-l-2 border-olive/50 pl-3 text-xs font-mono" data-testid="resend-sync-run-state">
          {running && <span className="text-amber-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Running…</span>}
          {run.status === "done" && <span className="text-olive-bright inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Done</span>}
          {run.status === "error" && <span className="text-blaze inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Errored</span>}
          <span className="text-secondary-foreground"> · {run.scanned || 0}/{run.total || 0} scanned · {run.synced || 0} synced · {run.excluded || 0} skipped{run.errors ? ` · ${run.errors} errors` : ""}</span>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={runPreview} disabled={busy === "preview"} data-testid="resend-sync-preview-btn"
          className="inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 disabled:opacity-50 font-heading font-bold uppercase text-sm px-3.5 py-2 transition-colors">
          {busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Preview distribution
        </button>
        <button onClick={runBackfill} disabled={busy === "backfill" || running || !status?.writes_allowed} data-testid="resend-sync-backfill-btn"
          className="inline-flex items-center gap-1.5 border border-volt/60 text-volt hover:bg-volt/10 disabled:opacity-40 disabled:cursor-not-allowed font-heading font-bold uppercase text-sm px-3.5 py-2 transition-colors"
          title={!status?.writes_allowed ? "Runs in production only" : "Sync all eligible members"}>
          {busy === "backfill" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run full sync
        </button>
      </div>
      {!status?.writes_allowed && (
        <p className="mt-2 text-[11px] font-mono text-amber-400" data-testid="resend-sync-prod-note">
          The full sync runs on opticswap.com only — the Resend account is shared, so preview/QA members are never pushed to the live audiences. Preview works everywhere.
        </p>
      )}
    </div>
  );
}

export default ResendSyncPanel;
